// src/pages/main-streaming-dashboard/components/RaiseHandsPanel.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Button, Tooltip, Modal, Input } from '../../../components/AntComponents';
import ViewDevice, { DeviceData } from '../../stream-management-grid/components/ViewDevice';
import { assignHostAndCoHost, mediamtxService } from '@/mediaservices/mediamtxService';
import { mediasoupService } from '@/mediaservices/mediasoupService';
import { BroadcastStorage } from '@/utils/BroadcastStorage';
import { MediaMTXUpdateBroadcast, MediaSoupBroadcast } from '@/api/broadcastService';

type DeviceType = 'mobile'| 'STB';
type ConnStrength = 'EXCELLENT' | 'GOOD' | 'FAIR' | 'POOR' | 'excellent' | 'good' | 'fair' | 'poor';

interface RaisedHand {
  id: string;                 // streamId / device_id
  participantName: string;    // derived from meta or id
  avatar?: string;
  deviceType: DeviceType;
  raisedAt: Date;
  connectionStrength: ConnStrength;
  deviceName?: string;
  location?: string;
  streamUrl?: string;         // WHEP for MediaMTX preview
  networkQuality?: string;    // Add network quality from WebSocket
  deviceId?: number;          // ✅ Add deviceId from WebSocket
}

interface RaiseHandsPanelProps {
  isCollapsed?: boolean;
  onToggleCollapse?: (collapsed: boolean) => void;
  className?: string;
  onCountChange?: (count: number) => void;
}

const USE_MEDIAMTX = String(import.meta.env.VITE_USE_MEDIAMTX).toLowerCase() === 'true';

const RaiseHandsPanel: React.FC<RaiseHandsPanelProps> = ({ isCollapsed, onToggleCollapse, className = '', onCountChange }) => {
  const [raisedHands, setRaisedHands] = useState<RaisedHand[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerData, setDrawerData] = useState<DeviceData | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  
  // ✅ Add search state
  const [searchQuery, setSearchQuery] = useState<string>('');

  // streamId/device_id -> meta (serial, type, group, location, whepUrl?)
  const metaById = useRef<Map<string, any>>(new Map());

  // Mediasoup previews: device_id -> MediaStream
  const mediasoupBySerial = useRef<Map<string, { mediaStream: MediaStream }>>(new Map());

  // ✅ Filter raised hands based on search query
  const filteredRaisedHands = useMemo(() => {
    if (!searchQuery.trim()) return raisedHands;
    
    const query = searchQuery.toLowerCase();
    return raisedHands.filter(hand => {
      const participantName = hand.participantName?.toLowerCase() || '';
      const deviceName = hand.deviceName?.toLowerCase() || '';
      const location = hand.location?.toLowerCase() || '';
      
      return participantName.includes(query) || 
             deviceName.includes(query) || 
             location.includes(query);
    });
  }, [raisedHands, searchQuery]);

  useEffect(() => {
    onCountChange?.(raisedHands.length);
  }, [raisedHands.length, onCountChange]);

  // ---------- Helpers ----------
  const upsertHand = (id: string, isRaised: boolean, networkQuality?: string, deviceId?: number, handRaiseTimestamp?: string, wsDeviceName?: string, wsDeviceType?: string) => {
    setRaisedHands(prev => {
      if (!isRaised) return prev.filter(h => h.id !== id);
      
      // Update existing hand if it exists
      const existingIndex = prev.findIndex(h => h.id === id);
      if (existingIndex !== -1) {
        const updated = [...prev];
        const meta = metaById.current.get(id);
        
        // ✅ Determine deviceType from WebSocket or existing data
        let deviceType: DeviceType = updated[existingIndex].deviceType;
        if (wsDeviceType) {
          deviceType = wsDeviceType.toLowerCase() === 'mobile' ? 'mobile' : 'STB';
        }
        
        updated[existingIndex] = {
          ...updated[existingIndex],
          networkQuality: networkQuality || updated[existingIndex].networkQuality,
          connectionStrength: networkQuality?.toLowerCase() as ConnStrength || updated[existingIndex].connectionStrength,
          deviceId: deviceId || updated[existingIndex].deviceId,
          raisedAt: handRaiseTimestamp ? new Date(handRaiseTimestamp) : updated[existingIndex].raisedAt,
          // ✅ Update with WebSocket deviceName if available
          participantName: wsDeviceName || updated[existingIndex].participantName,
          deviceType: deviceType,
        };
        return updated;
      }

      const meta = metaById.current.get(id);
      // ✅ Prioritize deviceName from WebSocket, fallback to meta
      const nickname = wsDeviceName || meta?.deviceSerialNumber || meta?.userId || id;

      // ✅ Prioritize deviceType from WebSocket, fallback to meta
      const deviceType: DeviceType = wsDeviceType
        ? (wsDeviceType.toLowerCase() === 'mobile' ? 'mobile' : 'STB')
        : (meta?.deviceType === 'Mobile' ? 'mobile'
          : meta?.deviceType === 'STB' ? 'STB'
          : 'STB');

      return [
        ...prev,
        {
          id,
          participantName: nickname,
          deviceType,
          raisedAt: handRaiseTimestamp ? new Date(handRaiseTimestamp) : new Date(),
          connectionStrength: networkQuality?.toLowerCase() as ConnStrength || 'good',
          networkQuality: networkQuality,
          deviceName: meta?.deviceGroup || meta?.deviceSerialNumber || undefined,
          location: meta?.location || meta?.deviceGroup || undefined,
          streamUrl: meta?.whepUrl, // MediaMTX only
          deviceId: deviceId, // ✅ Store deviceId from WebSocket
        }
      ];
    });
  };

  // ---------- MediaMTX MODE ----------
  useEffect(() => {
    if (!USE_MEDIAMTX) return;

    const seed = () => {
      const list = mediamtxService.getStreamSummaries();
      const map = new Map<string, any>();
      list.forEach(s => {
        map.set(String(s.id), { ...s.meta, whepUrl: s.whepUrl });
      });
      metaById.current = map;
      
      //Get current hands state on mount
      const currentHands = mediamtxService.getCurrentHandsState();
      
      if (currentHands.length > 0) {
        // Process current hands into the correct format
        const newRaisedHands: RaisedHand[] = currentHands.map(hand => {
          const meta = metaById.current.get(hand.id);
          // ✅ Prioritize deviceName from WebSocket
          const nickname = hand.deviceName || meta?.deviceSerialNumber || meta?.userId || hand.id;

          // ✅ Prioritize deviceType from WebSocket, fallback to meta
          const deviceType: DeviceType = hand.deviceType
            ? (hand.deviceType.toLowerCase() === 'mobile' ? 'mobile' : 'STB')
            : (meta?.deviceType === 'Mobile' ? 'mobile'
              : meta?.deviceType === 'STB' ? 'STB'
              : 'STB');

          return {
            id: hand.id,
            participantName: nickname,
            deviceType,
            raisedAt: hand.handRaiseTimestamp ? new Date(hand.handRaiseTimestamp) : new Date(),
            connectionStrength: hand.networkQuality?.toLowerCase() as ConnStrength || 'good',
            networkQuality: hand.networkQuality,
            deviceName: meta?.deviceGroup || meta?.deviceSerialNumber || undefined,
            location: meta?.location || meta?.deviceGroup || undefined,
            streamUrl: meta?.whepUrl,
            deviceId: hand.deviceId,
          };
        });
        
        setRaisedHands(newRaisedHands);
      }
    };
    seed();

    const onStreamUpdate = (p: { id: string; whepUrl: string; meta?: any }) => {
      metaById.current.set(String(p.id), { ...p.meta, whepUrl: p.whepUrl });
      setRaisedHands(prev => prev.map(h => {
        if (h.id !== p.id) return h;
        const meta = metaById.current.get(p.id);
        return {
          ...h,
          participantName: meta?.deviceSerialNumber || meta?.userId || h.participantName,
          deviceName: meta?.deviceGroup || h.deviceName,
          location: meta?.location || meta?.deviceGroup || h.location,
          streamUrl: meta?.whepUrl || h.streamUrl,
        };
      }));
    };

    const onHand = (p: { id: string; isHandRaised: boolean; networkQuality?: string; deviceId?: number; handRaiseTimestamp?: string; deviceName?: string; deviceType?: string }) => {
      upsertHand(String(p.id), !!p.isHandRaised, p.networkQuality, p.deviceId, p.handRaiseTimestamp, p.deviceName, p.deviceType);
    };

    const onRemoved = (p: { id: string }) => {
      setRaisedHands(prev => prev.filter(h => h.id !== String(p.id)));
      metaById.current.delete(String(p.id));
    };

    mediamtxService.on('streamUpdate', onStreamUpdate);
    mediamtxService.on('hand', onHand);
    mediamtxService.on('streamRemoved', onRemoved);

    return () => {
      mediamtxService.off('streamUpdate', onStreamUpdate);
      mediamtxService.off('hand', onHand);
      mediamtxService.off('streamRemoved', onRemoved);
    };
  }, []);

  // ---------- MEDIASOUP MODE ----------
  useEffect(() => {
    if (USE_MEDIAMTX) return;

    let mounted = true;

    const mapDeviceType = (t?: string): DeviceType => {
      switch ((t || '').toLowerCase()) {
        case 'mobile': return 'mobile';
        case 'stb':    return 'STB';
        default:       return 'STB';
      }
    };

    const rebuildFromMediasoupList = (raisedList: any[]) => {
      const metaMap = new Map<string, any>();

      const hands: RaisedHand[] = (raisedList || []).map((o) => {
        const isString = typeof o === 'string';
        const id = isString ? String(o) : String(o.device_id);
        const nickname = isString ? id : (o.device_serial || o.user_id || id);

        metaMap.set(id, {
          deviceSerialNumber: isString ? id : (o.device_serial || id),
          deviceType: isString ? undefined : o.device_type,
          deviceGroup: isString ? undefined : o.device_group,
          location: isString ? undefined : o.location,
        });

        return {
          id,
          participantName: nickname,
          deviceType: mapDeviceType(isString ? undefined : o.device_type),
          raisedAt: isString ? new Date() : (o.raised_at ? new Date(o.raised_at) : new Date()),
          connectionStrength: 'good',
          deviceName: isString ? undefined : (o.device_group || o.device_serial),
          location: isString ? undefined : (o.location || o.device_group),
          streamUrl: undefined, // Mediasoup uses mediaStream in drawer
        };
      });

      metaById.current = metaMap;
      setRaisedHands(hands);
    };

    const fetchHands = async () => {
      try {
        const resp = await mediasoupService.requestRaiseHandList();
        rebuildFromMediasoupList(resp?.raisedList || []);
      } catch (e) {
        console.error('requestRaiseHandList failed', e);
        if (mounted) setRaisedHands([]);
      }
    };

    const boot = async () => {
      try {
        await mediasoupService.initialize();
      } catch (e) {
        console.error('mediasoup initialize failed', e);
      }

      // preview cache
      mediasoupBySerial.current.clear();
      mediasoupService.getAvailableStreams().forEach((mediaStream, deviceId) => {
        mediasoupBySerial.current.set(String(deviceId), { mediaStream });
      });

      if (mediasoupService.isConnected()) {
        fetchHands();
      }

      const onConnected = () => fetchHands();
      //   GUARANTEED post-ACK trigger
      const onHandRequest = (data: any) => {
        // If it's a list update from ACK, rebuild the entire list
        if (data.type === 'list_updated' && data.raisedList) {
          rebuildFromMediasoupList(data.raisedList);
        } else {
          // Otherwise just fetch the list as before
          fetchHands();
        }
      };

      mediasoupService.on('connected', onConnected);
      mediasoupService.on('handRequest', onHandRequest);

      const onStreamAvailable = (p: { streamId: string; mediaStream: MediaStream }) => {
        mediasoupBySerial.current.set(String(p.streamId), { mediaStream: p.mediaStream });
      };
      const onStreamRemoved = (p: { streamId: string }) => {
        mediasoupBySerial.current.delete(String(p.streamId));
      };

      mediasoupService.on('streamAvailable', onStreamAvailable);
      mediasoupService.on('streamRemoved', onStreamRemoved);

      // cleanup
      return () => {
        mediasoupService.off('connected', onConnected);
        mediasoupService.off('handRequest', onHandRequest);
        mediasoupService.off('streamAvailable', onStreamAvailable);
        mediasoupService.off('streamRemoved', onStreamRemoved);
      };
    };

    let cleanup: (() => void) | undefined;
    boot().then(fn => { cleanup = fn; });

    return () => {
      mounted = false;
      if (cleanup) cleanup();
    };
  }, []);

  // ---------- WebSocket Hands State Sync ----------
  useEffect(() => {
    const service = USE_MEDIAMTX ? mediamtxService : mediasoupService;

    // ✅ Handle complete hands state from WebSocket
    const onHandsState = (handsFromWebSocket: any[]) => {
      
      const newRaisedHands: RaisedHand[] = handsFromWebSocket.map(hand => {
        const meta = metaById.current.get(hand.id);
        // ✅ Prioritize deviceName from WebSocket
        const nickname = hand.deviceName || meta?.deviceSerialNumber || meta?.userId || hand.id;

        // ✅ Prioritize deviceType from WebSocket, fallback to meta
        const deviceType: DeviceType = hand.deviceType
          ? (hand.deviceType.toLowerCase() === 'mobile' ? 'mobile' : 'STB')
          : (meta?.deviceType === 'Mobile' ? 'mobile'
            : meta?.deviceType === 'STB' ? 'STB'
            : 'STB');

        return {
          id: hand.id,
          participantName: nickname,
          deviceType,
          raisedAt: hand.handRaiseTimestamp ? new Date(hand.handRaiseTimestamp) : new Date(),
          connectionStrength: hand.networkQuality?.toLowerCase() as ConnStrength || 'good',
          networkQuality: hand.networkQuality,
          deviceName: meta?.deviceGroup || meta?.deviceSerialNumber || undefined,
          location: meta?.location || meta?.deviceGroup || undefined,
          streamUrl: meta?.whepUrl,
          deviceId: hand.deviceId,
        };
      });
      
      setRaisedHands(newRaisedHands);
    };

    // ✅ Use the new event
    service.on('handsState', onHandsState);

    return () => {
      service.off('handsState', onHandsState);
    };
  }, [USE_MEDIAMTX]);

  // ---------- Actions ----------
  const approve = async (id: string) => {
    try {
      const meta = metaById.current.get(id);
      const deviceSerial = meta?.deviceSerialNumber || id;

      if (USE_MEDIAMTX) {
        // First emit the admin_accept_hand WebSocket message
        mediamtxService.acceptHand(deviceSerial);
        
        // Wait for WebSocket processing
        await new Promise(resolve => setTimeout(resolve, 300));
        
        const { hostId } = mediamtxService.getCurrentRoles();
        
        // Assign new co-host (remove previous co-host and assign new one)
        await assignHostAndCoHost(hostId, deviceSerial);
        
        // Wait for role assignment to process
        await new Promise(resolve => setTimeout(resolve, 500));

        // --- Update broadcast if active ---
        const devices = BroadcastStorage.getSelectedDevices();
        const streamId = BroadcastStorage.getStreamId();
        

        if (devices.length && streamId) {
          const payload = {
            streamName: BroadcastStorage.getBroadcastName() || 'Updated Broadcast',
            devices: devices.map(s => ({
              deviceSerial: s,
              isHost: s === hostId,
              isCoHost: s === deviceSerial,
              action: 'update' as const,
            })),
            newHostDeviceSerial: hostId || undefined,
          };
          await MediaMTXUpdateBroadcast(streamId, payload);
        }
      } else {
        const { hostId, coHostId } = mediasoupService.getCurrentRoles();
        
        if (coHostId) {
          console.log("Existing co-host found, removing:", coHostId);
          await mediasoupService.removeCoHost(coHostId);
          await new Promise(resolve => setTimeout(resolve, 300));
        }
        
        await mediasoupService.promoteDeviceToCoHost(deviceSerial);
        await new Promise(resolve => setTimeout(resolve, 800));

        const devices = BroadcastStorage.getSelectedDevices();
        const broadcastName = BroadcastStorage.getBroadcastName();
        const hasActiveBroadcast = devices && devices.length > 0 && broadcastName;

        if (hasActiveBroadcast) {
          const updatedRoles = mediasoupService.getCurrentRoles();
          
          if (!updatedRoles.hostId) {
            console.warn('No host found after co-host promotion');
            return;
          }

          const hostProducerIds = mediasoupService.getHostProducerIds();
          const coHostProducerIds = mediasoupService.getCoHostProducerIds();

          const payload = {
            streamName: broadcastName,
            description: "Co-host replacement from raised hand acceptance",
            devices: devices,
            hostDeviceSerial: {
              deviceSerial: updatedRoles.hostId,
              audioProducerId: hostProducerIds[0] || '',
              videoProducerId: hostProducerIds[1] || '',
            },
            coHostDeviceSerial: updatedRoles.coHostId ? [{
              deviceSerial: updatedRoles.coHostId,
              audioProducerId: coHostProducerIds[0] || '',
              videoProducerId: coHostProducerIds[1] || '',
            }] : [],
          };

          if (updatedRoles.coHostId && payload.coHostDeviceSerial.length > 0) {
            try {
              const response = await MediaSoupBroadcast(payload);
              
              if (response?.streamId) {
                BroadcastStorage.storeStreamId(response.streamId);
              }
            } catch (broadcastError) {
              console.error('Failed to update broadcast after co-host replacement:', broadcastError);
            }
          }
        }
      }
    } catch (e) {
      console.error('approve failed', e);
    }
    
    // Clean up UI
    setRaisedHands(prev => prev.filter(h => h.id !== id));
    setSelectedId(null);
    setDrawerOpen(false);
  };

  const reject = (id: string) => {
    try {
      // Get the device serial from meta data
      const meta = metaById.current.get(id);
      const deviceSerial = meta?.deviceSerialNumber || id;
      
      console.log("Rejecting hand for:", { id, deviceSerial, meta });
      
      if (USE_MEDIAMTX) {
        //   Use rejectHand method
        mediamtxService.rejectHand(deviceSerial);
      } else {
        // For MediaSoup mode - pass deviceSerial
        mediasoupService.rejectHand(deviceSerial);
      }
    } catch (e) {
      console.error('reject hand failed', e);
    }
    setSelectedId(null);
    setDrawerOpen(false);
  };

  const openDrawerFor = async (hand: RaisedHand) => {
    setSelectedId(hand.id);
    
    const meta = metaById.current.get(hand.id);
    const deviceSerial = meta?.deviceSerialNumber || hand.id;
    
    const data: DeviceData = {
      // ✅ Use deviceId directly from WebSocket if available
      deviceId: hand.deviceId || undefined,
      participantName: deviceSerial, // ✅ Use device serial for participantName
      deviceType: hand.deviceType === 'STB' ? 'STB' : 'phone',
      deviceName: deviceSerial, // ✅ Use device serial for deviceName
      device_name: hand.participantName, // ✅ Use WebSocket deviceName for drawer title
      location: hand.location,
      connectionStrength: hand.connectionStrength,
      networkQuality: hand.networkQuality,
      raisedAt: hand.raisedAt,
      // ✅ Keep video working as before
      mediaStream: !USE_MEDIAMTX
        ? (mediasoupBySerial.current.get(hand.id)?.mediaStream ?? null)
        : null,
    };
    setDrawerData(data);
    setDrawerOpen(true);
  };

  // ---------- UI helpers ----------
  const getDeviceIcon = (deviceType: DeviceType) => {
    switch (deviceType) {
      case 'mobile': return 'Smartphone';
      case 'STB': return 'Video';
      default: return 'Video';
    }
  };

  const getConnectionColor = (strength: ConnStrength) => {
    const normalizedStrength = strength?.toLowerCase();
    switch (normalizedStrength) {
      case 'excellent': return 'text-green-500';
      case 'good': return 'text-blue-500';
      case 'fair': return 'text-yellow-500';
      case 'poor': return 'text-red-500';
      default: return 'text-muted-foreground';
    }
  };

  const getConnectionBars = (strength: ConnStrength) => {
    const normalizedStrength = strength?.toLowerCase();
    switch (normalizedStrength) {
      case 'excellent': return 4;
      case 'good': return 3;
      case 'fair': return 2;
      case 'poor': return 1;
      default: return 0;
    }
  };

  const formatTimeAgo = (date: Date) => {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  const selectedHand = useMemo(
    () => raisedHands.find(h => h.id === selectedId) || null,
    [raisedHands, selectedId]
  );

  const confirmAccept = (id: string, participantName: string) => {
    Modal.confirm({
      className: 'themed-confirm',
      title: 'Accept Raised Hand',
      content: (
        <span>
          By accepting, <b>{participantName}</b> will become the Co-Host. Continue?
        </span>
      ),
      okText: 'Accept',
      cancelText: 'Cancel',
      onOk: () => approve(id),
    });
  };

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Hand" size={18} className="text-primary" />
          <h3 className="text-sm font-medium text-foreground">Raised Hands</h3>
          {raisedHands.length > 0 && (
            <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
              {raisedHands.length}
            </span>
          )}
          {/* ✅ Show filtered count when searching */}
          {searchQuery.trim() && filteredRaisedHands.length !== raisedHands.length && (
            <span className="bg-muted text-muted-foreground text-xs px-2 py-0.5 rounded-full">
              {filteredRaisedHands.length} filtered
            </span>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={() => onToggleCollapse?.(!isCollapsed)}>
          <Icon name={isCollapsed ? "ChevronLeft" : "ChevronRight"} size={16} />
        </Button>
      </div>

      {/* Content */}
      {!isCollapsed && (
        <div className="p-4">
          {/* ✅ Search Input */}
          {raisedHands.length > 0 && (
            <div className="mb-4">
              <Input
                placeholder="Search by device name or location"
                prefix={<Icon name="Search" size={16} className="text-muted-foreground" />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                allowClear
                className="w-full"
                size="small"
              />
            </div>
          )}

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredRaisedHands.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="Hand" size={32} className="text-muted-foreground mx-auto mb-2" />
                {searchQuery.trim() ? (
                  <>
                    <p className="text-sm text-muted-foreground">No hands match your search</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Try searching for "{searchQuery}" in device names or locations
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">No raised hands</p>
                )}
              </div>
            ) : (
              filteredRaisedHands.map((hand) => (
                <div key={hand.id} className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:bg-muted transition-micro">
                  <Tooltip title="View Device">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDrawerFor(hand)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <Icon name="Eye" size={16} />
                    </Button>
                  </Tooltip>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      {/* ✅ Highlight search matches */}
                      <p className="text-sm font-medium text-foreground truncate">
                        {searchQuery.trim() ? (
                          <HighlightedText text={hand.participantName} query={searchQuery} />
                        ) : (
                          hand.participantName
                        )}
                      </p>
                      <Icon name={getDeviceIcon(hand.deviceType)} size={12} className="text-muted-foreground flex-shrink-0" />
                    </div>
                    <div className="flex items-center space-x-2 mt-0.5">
                      <span className="text-xs text-muted-foreground">{formatTimeAgo(hand.raisedAt)}</span>
                      
                      {/* ✅ Show device name and location with highlighting */}
                      {(hand.deviceName || hand.location) && (
                        <span className="text-xs text-muted-foreground">
                          {searchQuery.trim() ? (
                            <HighlightedText 
                              text={hand.deviceName || hand.location || ''} 
                              query={searchQuery} 
                            />
                          ) : (
                            hand.deviceName || hand.location
                          )}
                        </span>
                      )}
                      
                      {/* Enhanced network quality display */}
                      {/* <div className="flex items-center space-x-1">
                        <div className="flex space-x-0.5">
                          {[1,2,3,4].map(bar => (
                            <div key={bar}
                                 className={`w-0.5 h-2 rounded-sm ${
                                   bar <= getConnectionBars(hand.connectionStrength)
                                     ? getConnectionColor(hand.connectionStrength)?.replace('text-', 'bg-')
                                     : 'bg-muted'
                                 }`}
                            />
                          ))}
                        </div>
                        {hand.networkQuality && (
                          <span className={`text-xs ${getConnectionColor(hand.connectionStrength)}`}>
                            {hand.networkQuality.charAt(0).toUpperCase() + hand.networkQuality.slice(1).toLowerCase()}
                          </span>
                        )}
                      </div> */}
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <Tooltip title="Accept (Make Co-Host)">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => confirmAccept(hand.id, hand.participantName)}
                        className="text-success hover:text-success hover:bg-success/10"
                      >
                        <Icon name="Check" size={14} />
                      </Button>
                    </Tooltip>
                    <Tooltip title="Reject">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => reject(hand.id)}
                        className="text-error hover:text-error hover:bg-error/10"
                      >
                        <Icon name="X" size={14} />
                      </Button>
                    </Tooltip>
                  </div>
                </div>
              )))
            }
          </div>
        </div>
      )}

      <ViewDevice
        open={drawerOpen}
        mode="raisedHand"
        data={drawerData}
        onClose={() => setDrawerOpen(false)}
        onAccept={() => selectedHand && confirmAccept(selectedHand.id, selectedHand.participantName)}
        onReject={() => selectedHand && reject(selectedHand.id)}
      />
    </div>
  );
};

// ✅ Helper component to highlight search matches
const HighlightedText: React.FC<{ text: string; query: string }> = ({ text, query }) => {
  if (!query.trim()) return <>{text}</>;
  
  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  return (
    <>
      {parts.map((part, index) => 
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={index} className="bg-yellow-200 text-foreground px-0.5 rounded">
            {part}
          </mark>
        ) : (
          part
        )
      )}
    </>
  );
};

export default RaiseHandsPanel;
